import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { SimpleCancelButtonTheme } from './Common.types';
import { checkSelect } from './Common.utils';
import SimpleCancelButton from './SimpleCancelButton';
import {
  disableBodyScroll,
  enableBodyScroll,
  clearAllBodyScrollLocks,
} from 'body-scroll-lock';

interface ModalProps {
  isVisible: boolean;
  children: JSX.Element;
  onClose?: () => void;
  onSubmit?: () => void;
  mainActionTitle?: string;
  loading?: boolean;
  isMainActionDisabled?: boolean;
  modalSize?: 'small' | 'large';
  withoutCancelButton?: boolean;
  withoutContent?: boolean;
  customContainerStyle?: string;
  modalType?: 'EndUserModal' | 'AdminModal' | 'EndUserNormal';
  shouldHaveMaxZIndex?: boolean;
}

const Modal = ({
  onClose,
  onSubmit,
  isVisible,
  children,
  mainActionTitle,
  isMainActionDisabled = false,
  modalSize = 'large',
  withoutCancelButton,
  withoutContent,
  customContainerStyle,
  modalType,
  shouldHaveMaxZIndex,
}: ModalProps) => {
  const [translate] = useTranslation();
  const isSmallModal = modalSize === 'small';
  const isEndUserNormalModal = modalType === 'EndUserNormal';
  const modalRef = useRef<any>();

  useEffect(() => {
    if (modalRef.current) {
      if (isVisible) {
        disableBodyScroll(modalRef.current);
      } else {
        enableBodyScroll(modalRef.current);
      }
    }

    return () => {
      clearAllBodyScrollLocks();
    };
  }, [modalRef, isVisible]);

  const sectionStyle = customContainerStyle
    ? customContainerStyle
    : checkSelect(isSmallModal, 'w-534px pb-21px', 'w-632px');

  const buttonContainerStyle = `${checkSelect(
    withoutCancelButton || isEndUserNormalModal,
    'justify-start px-40px',
    'justify-center'
  )} ${checkSelect(
    modalType === 'EndUserModal',
    'bg-field-grey py-24px',
    'pb-33px pt-62px'
  )} ${isEndUserNormalModal && 'mb-5'}`;

  const mainActionStyle = checkSelect(
    isMainActionDisabled,
    'opacity-30 cursor-not-allowed',
    ''
  );

  const cancelButtonTitle = checkSelect(
    modalType === 'EndUserModal',
    translate('CANCEL_SMALL'),
    translate('CANCEL')
  );

  const zIndexStyle = checkSelect(shouldHaveMaxZIndex, 'z-max', 'z-99999');

  if (!isVisible) {
    return null;
  }

  return (
    <div
      className={`fixed top-0 left-0 w-full h-full bg bg-gray-900 bg-opacity-50 display flex flex-col items-center justify-center ${zIndexStyle}`}
      ref={modalRef}
    >
      <section
        className={`max-h-90% overflow-auto fixed bg-white h-auto rounded-4px ${sectionStyle}`}
      >
        {children}
        {!withoutContent && (
          <div className={`flex flex-row items-center ${buttonContainerStyle}`}>
            {!withoutCancelButton ? (
              isEndUserNormalModal ? (
                <SimpleCancelButton
                  onClick={onClose}
                  theme={SimpleCancelButtonTheme.BLUE}
                  className="mr-8"
                />
              ) : (
                <button
                  type="button"
                  onClick={onClose}
                  className="focus:outline-none font-secondary text-12px font-semibold text-header-parameter py-15px px-35px rounded-4px text-center bg-deselected-grey mr-12px"
                >
                  {cancelButtonTitle}
                </button>
              )
            ) : null}
            {mainActionTitle && (
              <button
                type="button"
                onClick={onSubmit}
                className={`focus:outline-none font-secondary font-semibold text-12px py-15px px-35px rounded-4px text-center text-white bg-main-ui-blue ${mainActionStyle}`}
                role="modal-submit-btn"
                disabled={isMainActionDisabled}
              >
                {mainActionTitle}
              </button>
            )}
          </div>
        )}
      </section>
    </div>
  );
};

export default Modal;
